<template>
  <div class="row-col">
    <a 
      data-dismiss="modal" 
      class="pull-right text-muted text-lg p-a-sm m-r-sm">×</a>
    <div class="p-a b-b">
      <span class="h5 text-capitalize"> {{ couponExists(coupon.id) ? 'Update' : 'Create' }} Coupon</span>
    </div>

    <div class="row-row">
      <div class="row-body">
        <div class="row-inner">

          <fieldset
            :disabled="!canManageCoupon"
            class="padding">
            <form>

              <!-- Name -->
              <div 
                :class="{'invalid':errors.has('name')}" 
                class="md-form-group">
                <input
                  v-validate="'required'"
                  :disabled="loading"
                  v-model="coupon.name"
                  type="text"
                  name="name"
                  class="md-input"
                  placeholder="Required">
                <label>Name</label>
                <span 
                  v-show="errors.has('name')" 
                  class="form-error-msg">{{ errors.first('name') }}</span>
              </div>

              <!-- Description -->
              <div class="md-form-group">
                <textarea 
                  :disabled="loading" 
                  v-model="coupon.description" 
                  rows="2" 
                  class="md-input"/>
                <label>Description</label>
              </div>

              <!-- Photos -->
              <div class="photos">
                <!-- preview image -->
                <div 
                  v-for="media in coupon.media_urls" 
                  :key="media.id" 
                  class="dz-custom-image-preview">
                  <img 
                    :src="media.original" 
                    alt="Coupon image">
                  <span 
                    class="remove-icon" 
                    @click="removeImage(media.id)"/>
                </div>

                <dropzone
                  id="couponImage"
                  ref="dropzone"
                  :url="api_media"
                  :accepted-file-types="'image/*'"
                  :max-number-of-files="20"
                  :auto-process-queue="false"
                  param-name="media"
                  @vdropzone-sending="onSendingFile"
                  @vdropzone-success="onUploadSuccess"/>
                <label>Photos</label>
              </div>

              <!-- Enable Coupon -->
              <div class="md-form-group p-b-sm">
                <label class="md-switch">
                  <input 
                    v-model="coupon.status"
                    type="checkbox"
                    name="status"
                  >
                  <i class="primary"/>
                  Enable Coupon For Sale
                </label>
              </div>

              <!-- Enable Limit Coupon Sale -->
              <div class="md-form-group p-b-sm">
                <label class="md-switch">
                  <input 
                    v-model="coupon.limit_sale"
                    type="checkbox"
                    name="limit_sale"
                  >
                  <i class="primary"/>
                  Limit Coupon Sale
                </label>
              </div>


              <!-- Limit Coupon Quantity -->
              <div 
                v-if="coupon.limit_sale" 
                :class="{'invalid':errors.has('limit_quantity')}" 
                class="md-form-group">
                <input
                  v-validate="minimumLimitQuantity"
                  :disabled="loading"
                  :name="'limit_quantity'"
                  v-model="coupon.limit_quantity"
                  type="number"
                  data-vv-as="Limit Quantity"
                  class="md-input quantity"
                >
                <label>Limit Quantity</label>
                <span 
                  v-if="errors.has('limit_quantity')" 
                  class="form-error-msg">{{ errors.first('limit_quantity') }}</span>
                <div class="progress mb-2 progress-sm m-t-sm">
                  <div 
                    :style="'width: ' + percentSold" 
                    class="progress-bar primary"/>
                </div>
                <span class="sold pull-left">Sold: {{ sold }}</span>
                <span class="remaining pull-right">Remaining: {{ remaining }}</span>
              </div>

              <!-- Link -->
              <div 
                v-if="couponLink && couponExists()" 
                class="md-form-group">
                <div class="copy-link">
                  <a 
                    v-clipboard:copy="couponLink"
                    v-clipboard:success="onLinkCopy"
                    v-clipboard:error="onLinkError"
                    class="btn btn-xs grey-300"
                  >
                    COPY LINK
                  </a>
                  <span 
                    v-if="link_copied" 
                    class="m-l-sm">Link copied!</span>
                </div>
                <input
                  :value="couponLink"
                  disabled
                  type="text"
                  class="md-input">
              </div>

              <!-- Outlets select -->
              <div class="form-group m-t-sm">
                <label class="typo__label multiselect-label">Outlets</label>
                <multiselect
                  id="ajaxOutlets"
                  v-model="coupon.outlets"
                  :options="outletList"
                  :multiple="true"
                  :loading="outlets_loading"
                  :close-on-select="false"
                  :limit="20"
                  :max-height="170"
                  :hide-selected="true"
                  label="name"
                  track-by="id"
                  placeholder="Type to search"
                  @search-change="loadOutlets"/>
                <small>Leave empty will make coupon redeemable at all outlets</small>
              </div>

              <!-- Products select -->
              <div class="form-group m-t-sm">
                <label class="typo__label multiselect-label">Products</label>
                <multiselect
                  id="ajaxProducts"
                  v-model="coupon.products"
                  :options="productList"
                  :multiple="true"
                  :loading="products_loading"
                  :close-on-select="false"
                  :limit="20"
                  :max-height="170"
                  :hide-selected="true"
                  label="name"
                  track-by="id"
                  placeholder="Type to search"
                  @search-change="loadProducts"/>
              </div>

              <!-- Tags select -->
              <div class="form-group m-t-sm">
                <label class="typo__label multiselect-label">Tags</label>
                <multiselect
                  id="ajaxTags"
                  v-model="coupon.tags"
                  :options="tagList"
                  :multiple="true"
                  :loading="tags_loading"
                  :close-on-select="false"
                  :limit="20"
                  :max-height="170"
                  :hide-selected="true"
                  label="name"
                  track-by="id"
                  placeholder="Type to search"
                  @search-change="loadTags"/>
              </div>

              <!-- Tax select -->
              <div class="form-group">
                <label class="typo__label multiselect-label">Taxes</label>
                <multiselect
                  v-model="coupon.taxes"
                  :options="taxList"
                  :multiple="true"
                  :loading="taxes_loading"
                  :close-on-select="false"
                  :hide-selected="true"
                  :max-height="170"
                  :limit="5"
                  label="name"
                  track-by="id"
                  placeholder="Type to search"/>
              </div>

              <!-- Availability Date -->
              <div class="row">
                <div class="col-sm-6">
                  <div class="md-form-group">
                    <label class="typo__label multiselect-label">Redemption Start Date</label>
                    <calendar 
                      v-model="coupon.available_from" 
                      :config="{'format': 'YYYY-MM-DD'}" 
                      :disabled="loading"/>
                    <small>The coupon will only be available for redemption on the start date onwards</small>
                  </div>
                </div>
                <div class="col-sm-6">
                  <div class="md-form-group">
                    <label class="typo__label multiselect-label">Redemption End Date</label>
                    <calendar 
                      v-model="coupon.available_to" 
                      :config="{'format': 'YYYY-MM-DD'}" 
                      :disabled="loading"/>
                    <small>The coupon will only be available for redemption before the end date</small>
                  </div>
                </div>
              </div>

              <!-- Price -->
              <div 
                :class="{'invalid':errors.has('price')}" 
                class="md-form-group">
                <input
                  v-validate="'required|decimal:2|min_value:0'"
                  :disabled="loading"
                  :name="'Price'"
                  v-model="coupon.price" 
                  type="number"
                  class="md-input"
                >
                <label>Price</label>
                <span 
                  v-show="errors.has('price')" 
                  class="form-error-msg">{{ errors.first('price') }}</span>
              </div>

              <!-- Value -->
              <div 
                :class="{'invalid':errors.has('value')}" 
                class="md-form-group">
                <input
                  v-validate="'required|decimal:2|min_value:0'"
                  :disabled="loading"
                  :name="'Value'"
                  v-model="coupon.value" 
                  type="number"
                  class="md-input"
                >
                <label>Value</label>
                <span 
                  v-show="errors.has('value')" 
                  class="form-error-msg">{{ errors.first('value') }}</span>
              </div>

              <!-- What you get -->
              <div class="md-form-group what-you-get">
                <div class="m-b-1">
                  <label class="m-b-0 p-t-sm">What Customer Will Get</label>&nbsp;
                  <select 
                    v-model="what_you_get_template" 
                    class="pull-right" 
                    @change="changeWhatYouGetTemplate()">
                    <option 
                      value="null" 
                      disabled>Pick a template</option>
                    <option value="buffet">Buffet</option>
                    <option value="cash">Cash Voucher</option>
                    <option value="alacarte">A la Carte</option>
                  </select>
                </div>
                <vue-editor
                  id="what-you-get"
                  v-model="coupon.what_you_get"
                  :editor-toolbar="customToolbar"
                  placeholder="List the item/discount that the customer will get"
                />
              </div>

              <!-- Fine print -->
              <div class="md-form-group fineprint">
                <div class="m-b-1">
                  <label class="m-b-0 p-t-sm">Fine Print</label>&nbsp;

                  <a 
                    v-if="!fineprint_template_loaded" 
                    class="pull-right btn btn-sm btn-default" 
                    @click="loadFinePrintTemplate()">Load Template</a>
                  <span 
                    v-if="fineprint_template_loaded" 
                    class="pull-right p-t-sm" 
                    @click="resetFinePrintTemplate()">Clear</span>
                </div>
                <vue-editor
                  id="fineprint"
                  v-model="coupon.fineprint"
                  :editor-toolbar="customToolbar"
                  placeholder="List your Terms & Conditions here"
                />

              </div>

            </form>

          </fieldset>
        </div>
      </div>
    </div>
    <div class="p-a b-t text-right">
      <button 
        :disabled="loading" 
        class="btn btn-fw white"
        data-dismiss="modal">Cancel</button>

      <button 
        :disabled="loading || !canManageCoupon"
        class="btn btn-fw primary"
        @click="submit()">Save</button>
    </div>
  </div>
</template>

<style>
.photos {
    position: relative;
    padding: 24px 0;
}
.vue-dropzone ~ label {
    font-size: 0.85em;
    z-index: 0;
    opacity: 0.5;
    position: absolute;
    top: 0px;
    left: 0px;
}
.vue-dropzone {
    padding: 15px !important;
    margin-bottom: 1rem;
}
.vue-dropzone .dz-preview {
    margin: 0 1rem 1rem 0;
    width: 25% !important;
}
.dz-custom-image-preview {
    width: 10rem;
    display: inline-block;
}
.ql-editor ul {
    padding-left: 0;
}
.what-you-get label,
.fineprint label {
    font-size: 0.85em;
}
.what-you-get span,
.fineprint span {
    text-decoration: underline;
    text-decoration-style: dotted;
}
.what-you-get span:hover,
.fineprint span:hover {
    text-decoration: underline;
    cursor: pointer;
}
.copy-link span {
    opacity: 0.8;
}
.md-input.quantity {
    width: 25%;
}
.sold,
.remaining {
    font-size: 0.85em;
    opacity: 0.5;
}
</style>

<script>
import CouponPermissionMixin from './coupon-permission';
import numeral from 'numeral';
import lodash from 'lodash';

export default {
    mixins: [CouponPermissionMixin],

    props: {
        coupon: {
            type: Object,
            required: true,
        },
        meta: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            api_media: Laroute.route('franchise.inventory.coupon.addmedia'),
            mediaId: 0,
            loading: false,
            remove_media: false,
            remove_media_ids: [],
            outletList: [],
            productList: [],
            tagList: [],
            taxList: [],
            outlets_loading: false,
            products_loading: false,
            tags_loading: false,
            taxes_loading: false,
            multiselect_query_limit: 30,
            link: '',
            link_copied: false,
            what_you_get_template_loaded: false,
            fineprint_template_loaded: false,
            customToolbar: [
                ['bold', 'italic', 'underline'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [],
            ],
            what_you_get_template: null,
        };
    },

    computed: {
        activeOutlet() {
            return this.$store.getters.activeOutlet;
        },
        couponLink() {
            return route('coupon.show', {
                franchise: this.meta.franchise ? this.meta.franchise.slug : '',
                coupon: this.coupon.slug,
            });
        },
        sold() {
            return this.couponExists() ? this.meta.bought : 'N/A';
        },
        remaining() {
            return this.couponExists()
                ? this.coupon.limit_quantity - this.meta.bought
                : 'N/A';
        },
        percentSold() {
            return numeral(
                this.meta.bought / this.coupon.limit_quantity,
            ).format('0%');
        },
        minimumLimitQuantity() {
            return this.couponExists()
                ? 'min_value:' + this.meta.bought
                : 'min_value:1';
        },
    },

    watch: {
        coupon() {
            this.resetForm();
        },
    },

    mounted() {
        this.loadOutlets();
        this.loadProducts();
        this.loadTags();
        this.loadTaxes();
    },

    methods: {
        submit() {
            this.loading = true;

            this.$validator.validateAll().then(result => {
                if (!result) {
                    this.scrollFormToTop();
                    this.stopLoading();
                    return;
                }

                let data = this.getSubmittedValue();

                if (this.hasRejectedFile()) {
                    this.scrollFormToTop();
                    this.stopLoading();
                    return;
                }

                this.$http[this.getSubmitMethod()](
                    this.getSubmitApi(),
                    data,
                ).then(
                    response => {
                        if (response.status == '200') {
                            this.remove_media_ids = [];
                            let data = response.data.data;

                            if (this.hasFileToUpload(data.id)) {
                                this.uploadFile(data.id);
                            } else {
                                this.successCallback();
                            }
                        }
                    },
                    error => {
                        toastr.error(error.statusText);
                        this.stopLoading();
                    },
                );
            });
        },

        getSubmitApi() {
            if (this.couponExists()) {
                return Laroute.route('franchise.inventory.coupon.update', {
                    id: this.coupon.id,
                });
            } else {
                return Laroute.route('franchise.inventory.coupon.store');
            }
        },

        couponExists(id) {
            return this.coupon.id != 0 && this.coupon.id != undefined;
        },

        scrollFormToTop() {
            $('#coupon-popup .row-body').animate({ scrollTop: 0 }, 'slow');
        },

        stopLoading() {
            this.loading = false;
        },

        getSubmittedValue() {
            let available_from = _.isNull(this.coupon.available_from)
                ? null
                : moment(this.coupon.available_from)
                      .startOf('day')
                      .format();
            let available_to = _.isNull(this.coupon.available_to)
                ? null
                : moment(this.coupon.available_to)
                      .endOf('day')
                      .format();

            return {
                name: this.coupon.name,
                description: this.coupon.description,
                status: this.coupon.status,
                limit_sale: this.coupon.limit_sale,
                limit_quantity: this.coupon.limit_quantity,
                available_from: available_from,
                available_to: available_to,
                price: this.coupon.price,
                value: this.coupon.value,
                fineprint: this.coupon.fineprint,
                what_you_get: this.coupon.what_you_get,
                remove_media: this.remove_media,
                remove_media_ids: this.remove_media_ids,
                outlets: this.coupon.outlets
                    ? this.coupon.outlets.map(obj => obj.id)
                    : [],
                products: this.coupon.products
                    ? this.coupon.products.map(obj => obj.id)
                    : [],
                tags: this.coupon.tags
                    ? this.coupon.tags.map(obj => obj.id)
                    : [],
                taxes: this.coupon.taxes
                    ? this.coupon.taxes.map(obj => obj.id)
                    : [],
            };
        },

        onSendingFile(file, xhr, formData) {
            formData.append('id', this.mediaId);
        },

        onUploadSuccess(file, response) {
            this.successCallback();
            this.$refs.dropzone.removeAllFiles();
        },

        successCallback() {
            this.loading = false;
            if (!this.couponExists()) {
                this.$parent.initCoupon();
            } else {
                this.$parent.hide_modal();
            }

            this.alertSuccess();
            this.$parent.refresh();
        },

        clearErrorValidation() {
            var that = this;
            setTimeout(() => {
                that.errors.clear();
            }, 50);
        },

        alertSuccess() {
            let msg =
                'Coupon successfully ' +
                (!this.couponExists() ? 'created' : 'updated');
            toastr.success(msg);
        },

        removeImage(id) {
            this.coupon.media_urls = lodash.filter(
                this.coupon.media_urls,
                media => {
                    return media.id != id;
                },
            );
            this.remove_media_ids.push(id);
            this.remove_media = true;
        },

        resetForm() {
            this.$refs.dropzone.removeAllFiles();
            this.remove_media = false;
            this.clearErrorValidation();
        },

        resetTemplate() {
            this.what_you_get_template = null;
            this.what_you_get_template_loaded = false;
            this.fineprint_template_loaded = false;
        },

        getOultetList() {
            let api = Laroute.route('franchise.vue.outlets');
            this.$http.get(api).then(response => {
                this.outlets = response.data.data;
            });
        },

        hasFileToUpload(id) {
            let files = this.$refs.dropzone.getAcceptedFiles();
            return (
                !_.isEmpty(files) && !_.isUndefined(id) && files[0].name != ''
            );
        },

        hasRejectedFile() {
            return this.$refs.dropzone.getRejectedFiles().length > 0;
        },

        uploadFile(couponId) {
            this.mediaId = couponId;
            this.$refs.dropzone.processQueue();
        },

        getSubmitMethod() {
            return this.couponExists() ? 'patch' : 'post';
        },

        onLinkCopy(e) {
            this.link_copied = true;
        },

        onLinkError(e) {},

        loadFinePrintTemplate() {
            this.coupon.fineprint =
                '<ul><li>Validity days: Monday – Sunday: 2pm – 10pm. Valid on public holidays.</li><li>Not valid on Dec 24-25, 31, 2017, Jan 1, 2018, Feb 14-18, 2018.</li><li>Strictly valid for dine-in only.</li><li>Valid in IPC Shopping Centre only.</li><li>No refunds for cancellations made after purchase.</li><li>Voucher is valid for A la Carte menu only.</li><li>Voucher may not be used for lunch menu, LTO menu, special menu.</li><li>Halal restaurant.</li><li>Limit to 6 vouchers per table per receipt.</li><li>Children (aged 4 and below) dine in for free. Max. 2 children dine for free per voucher.</li><li>Max. 2 hours dining period.</li><li>Prices displayed inclusive of GST and service charge.</li><li>Any purchases in excess of the voucher value must be settled directly with the merchant, subject to applicable taxes and charges.</li></ul>';
            this.fineprint_template_loaded = true;
        },

        resetFinePrintTemplate() {
            this.fineprint_template_loaded = false;
            this.coupon.fineprint = '';
        },

        changeWhatYouGetTemplate() {
            switch (this.what_you_get_template) {
                case 'buffet':
                    this.coupon.what_you_get =
                        '<p><strong>Buffet for 1 person includes</strong></p><ul><li>Food A</li><li>Food B</li><li>Variety of vegetables</li><li>Variety type of fish (eg. red snapper)</li></ul>';
                    break;
                case 'cash':
                    this.coupon.what_you_get = '<p>RM50 Cash Voucher</p>';
                    break;
                case 'alacarte':
                    this.coupon.what_you_get =
                        '<p><strong>Menu A la Carte for 1 person includes</strong></p><ul><li>Food A</li><li>Food B</li><li>Food C</li><li>Food D</li></ul>';
                    break;
            }
        },

        loadOutlets: _.debounce(function(query) {
            let api = Laroute.route('franchise.vue.outlets');
            let options = {
                params: {
                    query: query,
                    limit: this.multiselect_query_limit,
                },
            };

            this.outlets_loading = true;
            this.$http.get(api, options).then(response => {
                this.outletList = response.data.data;
                this.outlets_loading = false;
            });
        }, 500),

        loadProducts: _.debounce(function(query) {
            let api = Laroute.route('franchise.vue.products');
            let options = {
                params: {
                    query: query,
                    limit: this.multiselect_query_limit,
                },
            };

            this.products_loading = true;
            this.$http.get(api, options).then(response => {
                this.productList = response.data.data;
                this.products_loading = false;
            });
        }, 500),

        loadTags: _.debounce(function(query) {
            let api = Laroute.route('franchise.vue.tags');
            let options = {
                params: {
                    query: query,
                    limit: this.multiselect_query_limit,
                },
            };

            this.tags_loading = true;
            this.$http.get(api, options).then(response => {
                this.tagList = response.data.data;
                this.tags_loading = false;
            });
        }, 500),

        loadTaxes: _.debounce(function(query) {
            let api = Laroute.route('franchise.vue.gettaxselect');
            let options = {
                params: {
                    query: query,
                    limit: this.multiselect_query_limit,
                },
            };

            this.taxes_loading = true;
            this.$http.get(api, options).then(response => {
                this.taxList = response.data.data;
                this.taxes_loading = false;
            });
        }, 500),
    },
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/inventory/coupon/vuetablecoupon_form.vue